import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "config";

type SSELog = {
  msg: string;
  done?: boolean;
};

export function useSSEWithReconnect(jobId: string | null) {
  const [logs, setLogs] = useState<string[]>([]);
  const [status, setStatus] = useState<
    "idle" | "connecting" | "open" | "closed" | "error" | "connected"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  const retryCount = useRef(0);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!jobId) return;

    setLogs([]);
    setStatus("connecting");
    setError(null);

    const connect = () => {
      setStatus("connecting");
      const es = new EventSource(`${API_BASE_URL}/events/sse/${jobId}`);
      esRef.current = es;

      es.onopen = () => {
        setStatus("open");
        retryCount.current = 0;
      };

      es.onmessage = (e) => {
        const data: SSELog = JSON.parse(e.data);
        setLogs((prev) => [...prev, data.msg]);

        if (data.done) {
          setStatus("closed");
          es.close();
        }
      };

      es.onerror = () => {
        setStatus("error");
        setError("Connection lost. Reconnecting...");
        es.close();

        const delay = Math.min(30000, 1000 * Math.pow(2, retryCount.current));
        retryCount.current += 1;

        setTimeout(() => connect(), delay);
      };
    };

    connect();

    return () => {
      esRef.current?.close();
    };
  }, [jobId]);

  return { logs, status, error };
}
